'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { parseQuickFilterPresets, type QuickFilterPreset } from '@/components/filters/QuickFilters';
import { useMetadata } from '@/lib/hooks/useMetadata';
export interface UseQuickFiltersOptions {
  /** Metadata key holding the preset JSON (via the metadata API). */
  metadataKey: string;
  /** Presets returned when no metadata is configured or the stored value is invalid. */
  defaults: readonly QuickFilterPreset[];
  /**
   * Optional whitelist of facet keys accepted from the stored JSON. Any facet
   * outside this list is ignored during parsing.
   */
  allowedFacetKeys?: readonly string[];
  /**
   * Controls what `presets` returns while the metadata request is in flight.
   *
   * - `true` (default): optimistic — render `defaults` immediately, then swap to
   *   the metadata-driven presets once they load.
   * - `false`: wait for the server — `presets` is empty until the request
   *   settles, avoiding the two-phase flash of defaults being replaced.
   */
  optimistic?: boolean;
}
export interface UseQuickFiltersResult {
  /** Presets to render — metadata-driven when available, otherwise the defaults. */
  presets: readonly QuickFilterPreset[];
  /** True while the metadata request is in flight. */
  isLoading: boolean;
  /** True when the presets came from stored metadata rather than the defaults. */
  isCustom: boolean;
}

/**
 * Dynamically retrieve QuickFilters presets from the metadata API, validating
 * the untrusted JSON and falling back to `defaults` when it's missing, empty or
 * malformed. Lets quick picks be curated per surface without a redeploy.
 *
 * By default the hook is optimistic (renders defaults while loading). Pass
 * `{ optimistic: false }` to hold off rendering any presets until the server
 * response settles, avoiding the flash of defaults being replaced.
 */
export function useQuickFilters(t0) {
  const $ = _c(8);
  const {
    metadataKey,
    defaults,
    allowedFacetKeys,
    optimistic: t1
  } = t0;
  const optimistic = t1 === undefined ? true : t1;
  const {
    data,
    isLoading
  } = useMetadata(metadataKey);
  let t2;
  if ($[0] !== allowedFacetKeys || $[1] !== data) {
    t2 = parseQuickFilterPresets(data, allowedFacetKeys);
    $[0] = allowedFacetKeys;
    $[1] = data;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const parsed = t2;
  const isCustom = !!parsed && parsed.length > 0;
  let t3;
  bb0: {
    if (isCustom) {
      t3 = parsed;
      break bb0;
    }
    if (!optimistic && isLoading) {
      let t4;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[3] = t4;
      } else {
        t4 = $[3];
      }
      t3 = t4;
      break bb0;
    }
    t3 = defaults;
  }
  const presets = t3;
  let t4;
  if ($[4] !== isCustom || $[5] !== isLoading || $[6] !== presets) {
    t4 = {
      presets,
      isLoading,
      isCustom
    };
    $[4] = isCustom;
    $[5] = isLoading;
    $[6] = presets;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}